import { useEffect, useMemo, useState } from "react";
import { ApiPaymentRequest } from "src/api/types";
import Button from "src/components/Button";
import Field from "src/components/field/Field";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import Layout from "src/components/layout/Layout";
import SubscriptionFeatureMiddleware from "src/components/middlewares/SubscriptionFeatureMiddleware";
import PaymentRequestProcessTable from "src/components/payment-request/visualizations/PaymentRequestProcessTable";
import useUnreads from "src/hooks/api/useUnreads";
import useAuth from "src/hooks/selectors/useAuth";
import useModules from "src/hooks/selectors/useModules";

const PaymentRequestsProcessView = ({ t }: Translation) => {
	const auth = useAuth();
	const { modules, loading: loadingModules } = useModules();
	const { fetchUnreads } = useUnreads();
	const [filter, setFilter] = useState<{
		connection_id?: number | string;
		status?: ApiPaymentRequest["status"][];
	}>({ status: ["APPROVED"] });

	const createUrl = useMemo(() => {
		const path = "/payment-requests/create";
		if (!filter) return path;
		const search = new URLSearchParams();
		if (filter?.connection_id) {
			search.set("connection_id", filter.connection_id.toString());
		}
		return `/payment-requests/process/create?${search.toString()}`;
	}, [filter]);

	useEffect(() => {
		fetchUnreads();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (
		auth.type === "company" &&
		!modules.includes("payment-requests") &&
		!loadingModules
	) {
		return (
			<Layout
				title={t("title")}
				breadcrumb={[
					{
						label: t("title"),
						to: "/payment-requests/process",
					},
				]}
			>
				<SubscriptionFeatureMiddleware plan="premium" />
			</Layout>
		);
	}

	return (
		<Layout
			title={t("title")}
			breadcrumb={[
				{
					label: t("title"),
					to: "/payment-requests/process",
				},
			]}
		>
			<div className="flex justify-between">
				<div className="flex items-center gap-4">
					<Field.Connection
						onChange={(value) =>
							setFilter((filter) => ({
								...filter,
								connection_id: value,
							}))
						}
						value={filter?.connection_id}
						placeholder={t(
							`filters.${
								auth.type === "student" ? "company" : "student"
							}`
						)}
					/>
				</div>
				<div className="flex items-center gap-4">
					<Button
						to={{
							modal: createUrl,
						}}
					>
						{t("create")}
					</Button>
				</div>
			</div>
			<div className="flex flex-col flex-1">
				<PaymentRequestProcessTable {...{ filter }} />
			</div>
		</Layout>
	);
};

PaymentRequestsProcessView.locale = {
	nl: {
		title: "Verzoeken verwerken",
		create: "Aanmaken",
		tabs: {
			agenda: "Agenda",
			list: "Lijst",
		},
		filters: {
			company: "Opdrachtgever",
			student: "Opdrachtnemer",
			status: "Status",
		},
	},
	en: {
		title: "Process Requests",
		create: "Create",
		tabs: {
			agenda: "Agenda",
			list: "List",
		},
		filters: {
			company: "Client",
			student: "Contractor",
			status: "Status",
		},
	},
};

export default withTranslation(PaymentRequestsProcessView);
